// THE one brand table for the back office. A single deployment build serves every
// platform domain; which brand it wears is decided ONCE here, by hostname, and read
// everywhere else as `BRAND.<field>` — never re-derived with a scattered
// `host.includes('fanpass') ? … : …` ternary. Add a brand = add a row.
//
//   moments.ug / momo.loft.ug → Moments (MoMo)    fanpass.ke → Fanpass    *.tkt.ug → TKT
//   backoffice.mookh.com → MOOKH                  backoffice.party.ug → MOOKH (party. deployment)
//
// This mirrors the backend's brand identity (see tkt-backend/src/utils/brandProfile.ts):
// keep the two tables in step. Logos live in /public. localhost / previews → TKT.

export type Brand = {
  key: 'tkt' | 'fanpass' | 'moments' | 'mookh' | 'party' | 'mandela';
  name: string;            // display name shown in chrome + settings
  logo: string;            // /public asset — the SQUARE mark, for tile slots + favicon
  // Optional horizontal wordmark, for the slots that actually have room for one
  // (sign-in card header, expanded sidebar). Brands whose logo IS a square mark
  // simply omit it and every slot keeps rendering the tile, exactly as before.
  wordmark?: string;
  accent: string;          // brand primary colour
  font: string;            // primary type face (the body reads it via --brand-font)
  googleFont?: string;     // Google-Fonts family to lazy-load (when not a local @font-face)
  domain: string;          // primary web domain (tkt.ug)
  supportEmail: string;    // support@…
  ticketSender: string;    // the from-address tickets are sent from
  noReply: string;         // no-reply@…
  auditBucket: string;     // object-store path for audit exports
};

// MOOKH's VISUAL identity, shared by both of its deployments. The two hosts are one
// brand to look at and two different senders to mail from — party.ug and mookh.com
// have their own verified domains (mirrors commPolicy's `party` deployment vs `mookh`
// brand rows). Splitting identity from sending identity here is what lets one wordmark
// serve both without either row lying about where its mail comes from.
const MOOKH_LOOK = {
  name: 'MOOKH',
  logo: '/mookh.svg',              // the "oo" capsule on the brand's yellow tile — their own favicon
  wordmark: '/mookh-wordmark.png', // the black wordmark; the app's chrome is light
  accent: '#1D1D1F',               // brand ink (globals.css --primary is black); the yellow #FAED8A
                                   // is a highlight, unusable as a text/primary colour
  // The brand's REAL type face: DM Sans with the MOOKH OpenType ligatures baked in
  // (every "oo" → the capsule; "MOOKH" → the wordmark, as live text). Deliberately
  // NOT family-named 'DM Sans': Fanpass loads stock DM Sans from Google Fonts, and a
  // same-named local @font-face would silently give Fanpass MOOKH's ligatures too.
  font: "'DM Sans Mookh', 'DM Sans'",
} as const;

const BRANDS: Record<Brand['key'], Brand> = {
  tkt: {
    key: 'tkt', name: 'TKT', logo: '/tkt.png', accent: '#1F2937',
    font: "'Inter'", // already loaded in index.html
    domain: 'tkt.ug', supportEmail: 'support@tkt.ug', ticketSender: 'tickets@tkt.ug',
    noReply: 'no-reply@tkt.ug', auditBucket: 's3://tkt-audit-prod',
  },
  fanpass: {
    key: 'fanpass', name: 'Fanpass', logo: '/fanpass.png', accent: '#0B7A4B',
    font: "'DM Sans'", googleFont: 'DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700',
    domain: 'fanpass.ke', supportEmail: 'support@fanpass.ke', ticketSender: 'tickets@fanpass.ke',
    noReply: 'no-reply@fanpass.ke', auditBucket: 's3://fanpass-audit-prod',
  },
  moments: {
    key: 'moments', name: 'Moments', logo: '/momologo.png', accent: '#004F71',
    font: "'MTN Brighter Sans'", // local @font-face in index.css (mirrors tkt-new-ui)
    domain: 'moments.ug', supportEmail: 'support@moments.ug', ticketSender: 'tickets@moments.ug',
    noReply: 'no-reply@moments.ug', auditBucket: 's3://moments-audit-prod',
  },
  mookh: {
    key: 'mookh', ...MOOKH_LOOK,
    // Sends from party.ug, not mookh.com. The backend only routes the sender domains in
    // sendEmail.ts's SENDER_MAILBOX_BY_DOMAIN (chan2025.com / fanpass.ke / moments.ug /
    // party.ug) — anything else silently falls through to TKT's courier@ticket.ug. So
    // until mookh.com is verified there, MOOKH's mail honestly goes out on its verified
    // sibling domain rather than naming a mailbox that doesn't exist. `hello@mookh.africa`
    // IS real — it's the brand's replyTo in commPolicy.
    domain: 'mookh.com', supportEmail: 'hello@mookh.africa', ticketSender: 'tickets@party.ug',
    noReply: 'no-reply@party.ug', auditBucket: 's3://mookh-audit-prod',
  },
  party: {
    // party.ug is its own deployment wearing MOOKH chrome — same look, own sender
    // domain (commPolicy `party`: sender party.ug, replyTo hello@party.ug).
    key: 'party', ...MOOKH_LOOK,
    domain: 'party.ug', supportEmail: 'hello@party.ug', ticketSender: 'tickets@party.ug',
    noReply: 'no-reply@party.ug', auditBucket: 's3://party-audit-prod',
  },
  mandela: {
    // Mandela National Stadium (Kampala) — the CHAN 2026 flagship venue's own tenant.
    // logo.svg is the real mark fetched from mandelastadium.ug/mandela.png; accent is
    // its wordmark ink (#342755) rather than the stadium-silhouette green (#aebf36),
    // which is too light for text/UI chrome — the same reasoning MOOKH's brand ink vs
    // highlight split already documents above.
    key: 'mandela', name: 'Mandela', logo: '/mandela.png', accent: '#342755',
    font: "'Inter'",
    domain: 'mandelastadium.ug', supportEmail: 'support@mandelastadium.ug',
    ticketSender: 'tickets@mandelastadium.ug', noReply: 'no-reply@mandelastadium.ug',
    auditBucket: 's3://mandela-audit-prod',
  },
};

// Every real brand row, for surfaces that must look across ALL deployments at once
// (e.g. System health polling each one's own /health) rather than just the one this
// build is currently wearing.
export const ALL_BRANDS: Brand[] = Object.values(BRANDS);

export const detectBrand = (host?: string): Brand => {
  // A `?brand=` query param (persisted to localStorage `brandOverride`) lets the one
  // build preview any brand on localhost, since brand is normally hostname-only.
  // `?brand=clear` removes the override.
  if (typeof window !== 'undefined') {
    try {
      const q = new URLSearchParams(window.location.search).get('brand');
      if (q === 'clear') window.localStorage.removeItem('brandOverride');
      else if (q && BRANDS[q as Brand['key']]) window.localStorage.setItem('brandOverride', q);
      const ov = window.localStorage.getItem('brandOverride');
      if (ov && BRANDS[ov as Brand['key']]) return BRANDS[ov as Brand['key']];
    } catch { /* storage unavailable — fall through to hostname */ }
  }
  const h = String(host || (typeof window !== 'undefined' ? window.location.host : '') || '').toLowerCase().split(':')[0];
  // MOOKH first, and matched on the FULL domain, not a bare word: commPolicy learned the
  // hard way that plain "party" collides with ordinary names. `mookh` can't collide with
  // the `momo`/`moments` tests below (no shared substring), but ordering it first keeps
  // the read unambiguous.
  if (h.includes('mookh.com') || h.includes('mookh.africa')) return BRANDS.mookh;
  if (h.includes('party.ug')) return BRANDS.party;
  if (h.includes('moments') || h.includes('momo')) return BRANDS.moments;
  if (h.includes('fanpass')) return BRANDS.fanpass;
  if (h.includes('mandelastadium')) return BRANDS.mandela;
  return BRANDS.tkt; // tkt.ug, localhost, previews
};

export const BRAND: Brand = detectBrand();

// Reflect the brand once at load: favicon AND the type face — every surface inherits
// the font via the CSS var, so this one line re-skins the whole app. The tab TITLE is
// not set here: it belongs to the router (lib/pageTitle) because it changes with the
// route, and a brand line that also wrote it would be a second producer of one value.
if (typeof document !== 'undefined') {
  let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
  link.href = BRAND.logo;

  document.documentElement.style.setProperty('--brand-font', BRAND.font);
  // Lazy-load a Google-Fonts face (Fanpass → DM Sans) only for the active brand.
  if (BRAND.googleFont && !document.getElementById('brand-font-link')) {
    const fl = document.createElement('link');
    fl.id = 'brand-font-link'; fl.rel = 'stylesheet';
    fl.href = `https://fonts.googleapis.com/css2?family=${BRAND.googleFont}&display=swap`;
    document.head.appendChild(fl);
  }
}
